import { FormEvent, useCallback, useState } from 'react';
import { Camera, Trash2, Video, X as CloseIcon } from 'lucide-react';
import { v4 as uuid } from 'uuid';

import { TranslationsKeys } from 'types/translation';
import { IMAGE_CATEGORY, VIDEO_CATEGORY } from 'types/attachmentTypes';
import { Attachment } from 'types/surveyResponse';
import { createVideoThumbnail, resizeImage } from 'utils/image';
import { getBlobHotlinkUrl } from 'utils/blob';
import { classNameList } from 'utils/property';
import { DateTimeFormat, formatDateTime } from 'utils/date';
import { useBlobMutation, useDeleteBlobMutation } from 'hooks/mutations';
import { useBlobDataUrlQuery } from 'hooks/queries';
import { useTranslate } from 'hooks/useTranslate';
import { FullscreenContainer } from 'components/FullscreenContainer';
import { MessageBar } from 'components/MessageBar';
import { Button } from 'components/ui/Button';
import styles from './Images.module.scss';

interface IProps {
  label?: string;
  images: Attachment[];
  onImagesChanged?: (attachments: Attachment[]) => void;
}

const IMAGE_THUMBNAIL_SIZE = 160; // 80px, account for Retina screens
const IMAGE_THUMBNAIL_PREFIX = 'thumb-';

const extensions = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'video/webm': '.webm',
  'video/mp4': '.mp4'
};

// displays a single image, from its name (blob id), as a <div> of a given class with background-image,
const Image = ({ name, onClick }: { name: string; onClick?: () => void }) => {
  // force .jpg extension for video thumbnails
  const isVideo = name.endsWith('.mp4') || name.endsWith('.webm');
  if (isVideo) {
    name = name.slice(0, name.indexOf('.')) + '.jpg';
  }
  const blobDataUrlQuery = useBlobDataUrlQuery(IMAGE_CATEGORY, name);

  return (
    <div
      className={classNameList(styles.imagesThumbnail, isVideo && styles.imagesThumbnailVideo)}
      role="img"
      style={{ backgroundImage: `url(${blobDataUrlQuery.data ?? getBlobHotlinkUrl(IMAGE_CATEGORY, name)})` }}
      onClick={onClick}
    />
  );
};

const ImageFullscreen = ({ name, onClick }: { name: string; onClick?: () => void }) => {
  const blobDataUrlQuery = useBlobDataUrlQuery(IMAGE_CATEGORY, name);
  const isVideo = name.endsWith('.mp4') || name.endsWith('.webm');

  if (isVideo) {
    return (
      <div className={styles.imagesFullscreenImageContainer}>
        <video autoPlay controls className={styles.imagesFullscreenImage}>
          <source src={blobDataUrlQuery.data ?? getBlobHotlinkUrl(VIDEO_CATEGORY, name)} />
        </video>
      </div>
    );
  } else {
    return (
      <div className={styles.imagesFullscreenImageContainer}>
        <img
          alt=""
          className={styles.imagesFullscreenImage}
          src={blobDataUrlQuery.data ?? getBlobHotlinkUrl(IMAGE_CATEGORY, name)}
          onClick={onClick}
        />
      </div>
    );
  }
};

export const Images = ({ label, images, onImagesChanged }: IProps) => {
  const { t } = useTranslate();
  const blobMutation = useBlobMutation();
  const deleteBlobMutation = useDeleteBlobMutation();
  const [uploadWorking, setUploadWorking] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<Attachment | null>(null);

  // image upload
  const onImageUpload = useCallback(
    async (e: FormEvent<HTMLInputElement>) => {
      try {
        setUploadWorking(true);
        setUploadError(false);
        // "upload" images in parallel, in practice these should anyway go just to IndexedDB
        // but resizing for thumbnails would still be not quite instant
        const newImages = await Promise.all(
          Array.from(e.currentTarget.files!).map(async (f): Promise<Attachment> => {
            const name = uuid() + (extensions[f.type as keyof typeof extensions] || '');
            await blobMutation.mutateAsync({ category: IMAGE_CATEGORY, blobId: name, blob: f });
            // generate thumbnail right away and store it under the same name but with 'thumb-' prefix
            // with our offline-first app need to do it on the frontend
            const thumb = await resizeImage(f, IMAGE_THUMBNAIL_SIZE, f.type);
            await blobMutation.mutateAsync({
              category: IMAGE_CATEGORY,
              blobId: IMAGE_THUMBNAIL_PREFIX + name,
              blob: thumb
            });
            return { name, mimeType: f.type, createdAt: new Date().toISOString() };
          })
        );
        onImagesChanged!([...images, ...newImages]);
      } catch (e) {
        console.error('Upload failed', e);
        setUploadError(true);
      } finally {
        setUploadWorking(false);
      }
    },
    [images, blobMutation]
  );

  // video upload
  const onVideoUpload = useCallback(
    async (e: FormEvent<HTMLInputElement>) => {
      try {
        setUploadWorking(true);
        setUploadError(false);
        // "upload" videos in parallel, in practice these should anyway go just to IndexedDB
        // but getting thumbnails would still be not quite instant
        const newImages = await Promise.all(
          Array.from(e.currentTarget.files!).map(async (f): Promise<Attachment> => {
            const basename = uuid();
            const name = basename + (extensions[f.type as keyof typeof extensions] || '');
            // generate thumbnail right away and store it under the same name but with 'thumb-' prefix
            // with our offline-first app need to do it on the frontend
            const thumb = await createVideoThumbnail(f);
            await blobMutation.mutateAsync({
              category: IMAGE_CATEGORY,
              blobId: IMAGE_THUMBNAIL_PREFIX + basename + '.jpg',
              blob: thumb
            });
            await blobMutation.mutateAsync({ category: VIDEO_CATEGORY, blobId: name, blob: f });
            return { name, mimeType: f.type, createdAt: new Date().toISOString() };
          })
        );
        onImagesChanged!([...images, ...newImages]);
      } catch (e) {
        console.error('Upload failed', e);
        setUploadError(true);
      } finally {
        setUploadWorking(false);
      }
    },
    [images, blobMutation]
  );

  const onDelete = useCallback(
    (image: Attachment) => {
      // no async here, for actual blob deletion (image and thumbnail) fire and forget and ignore errors
      deleteBlobMutation.mutate(image.name);
      deleteBlobMutation.mutate(IMAGE_THUMBNAIL_PREFIX + image.name);
      onImagesChanged!(images.filter((i) => i.name !== image.name));
      setFullscreenImage(null);
    },
    [images]
  );

  return (
    <div className={styles.images}>
      {label && <label className={styles.imagesTitle}>{label}</label>}
      {!images.length && <div className={styles.imagesNoImages}>{t(TranslationsKeys.images_noImages)}</div>}
      {images.length > 0 && (
        <div className={styles.imagesThumbnails}>
          {images.map((i) => (
            <Image key={i.name} name={IMAGE_THUMBNAIL_PREFIX + i.name} onClick={() => setFullscreenImage(i)} />
          ))}
        </div>
      )}

      {uploadError && (
        <MessageBar variant="danger" title="" description={t(TranslationsKeys.images_error)} showCloseAction={false} />
      )}

      {onImagesChanged && (
        <div className={styles.imagesButtons}>
          <Button
            variant="secondary"
            size="medium"
            state={uploadWorking ? 'loading' : 'normal'}
            className={styles.imagesAdd}>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png"
              disabled={uploadWorking}
              className={styles.imagesFileInput}
              onInput={onImageUpload}
            />
            <Camera /> {t(TranslationsKeys.images_add)}
          </Button>

          <Button
            variant="secondary"
            size="medium"
            state={uploadWorking ? 'loading' : 'normal'}
            className={styles.imagesAdd}>
            <input
              type="file"
              accept="video/*"
              disabled={uploadWorking}
              className={styles.imagesFileInput}
              onInput={onVideoUpload}
            />
            <Video /> {t(TranslationsKeys.images_video_add)}
          </Button>
        </div>
      )}

      {fullscreenImage && (
        <FullscreenContainer fullWidth>
          <div className={styles.imagesFullscreen}>
            <div className={styles.imagesFullscreenHeading}>
              <span>
                {fullscreenImage.createdAt && formatDateTime(fullscreenImage.createdAt, DateTimeFormat.MEDIUM)}
              </span>
              <button onClick={() => setFullscreenImage(null)}>
                <CloseIcon />
              </button>
            </div>
            <ImageFullscreen name={fullscreenImage.name} onClick={() => setFullscreenImage(null)} />
            <div className={styles.imagesFullscreenFooter}>
              <button
                className={classNameList(
                  styles.imagesFullscreenDeleteButton,
                  !onImagesChanged && styles.imagesFullscreenDeleteButtonHidden
                )}
                onClick={() => onDelete(fullscreenImage)}>
                <Trash2 />
              </button>
            </div>
          </div>
        </FullscreenContainer>
      )}
    </div>
  );
};
